import { IDControler, generateEdge, generateNodes } from './generate'
import type { XYPosition } from 'reactflow'
import type { CustomEdgeType } from 'src/newComponents/edges/customEdge'
import type { NODE_TYPE } from 'src/newComponents/nodes'
import type {
  CustomNodeType,
  InputNodeType,
  OutputNodeType,
} from 'src/newComponents/nodes/types'
import type { RawAction, RawData, RawNode, Transfer } from 'src/types'

const rawType: {
  [key: string]: string
} = {
  messageAction: '5',
  JavaPluginAction: '4',
  LLMAction: '2',
  inputNode: 'input',
  outputNode: 'output',
  5: 'kd.ai.gai.core.engine.flow.MessageAction',
  4: 'kd.ai.gai.core.engine.flow.JavaPluginAction',
  2: 'kd.ai.gai.core.engine.flow.LLMAction',
}

const SAVE_TYPE_MAP = {
  input: '0',
  output: '1',
  LLMAction: '2',
  JavaPluginAction: '4',
  MessageAction: '5',
  Condition: '6',
}

export function transformRawDataToFlowData(rawData: RawData, isLock: boolean) {
  const nodes: CustomNodeType[] = []
  const edges: CustomEdgeType[] = []
  const input = rawData.start
  const output = rawData.end
  let positionY = 0
  const inputNode = generateNodes.input()
  inputNode.data.nodeName = input.name

  function setPosition(node: CustomNodeType, position?: XYPosition) {
    if (position) {
      node.position = position
    }
    else {
      node.position = {
        ...inputNode.position,
        y: positionY,
      }
      positionY = positionY + 150
    }
  }

  // inputNode

  setPosition(inputNode, input.position)
  nodes.push(inputNode)
  rawData.actions.forEach((action) => {
    const param = action.type.split('.')
    const properties = action.properties
    const type = param[param.length - 1] as keyof typeof NODE_TYPE
    const actionNode = generateNodes[type](String(properties.id))
    actionNode.data.nodeName = properties.name
    setPosition(actionNode, properties.position)
    if (isLock) {
      actionNode.deletable = false
    }
    nodes.push(actionNode)
  })
  rawData.transfers.forEach((transfer) => {
    const actionEdge = generateEdge(
      String(transfer.id),
      transfer.condition ? transfer.condition.name : '',
      String(transfer.fromNodeId),
      String(transfer.toNodeId),
      'bottom',
      'top',
    )
    IDControler.setId(Math.max(IDControler.getNow(), transfer.id))
    if (isLock) {
      actionEdge.deletable = false
    }
    edges.push(actionEdge)
  })
  const outputNode = generateNodes.output()
  outputNode.data.nodeName = output.name
  setPosition(outputNode, output.position)
  nodes.push(outputNode)
  return {
    nodes,
    edges,
  }
}

export function transformNodeWhenNewNode(node: CustomNodeType, _nodes: CustomNodeType[], edges: CustomEdgeType[]) {
  const nodeRawdata = createRawNodeData(
    node.id,
    node.data.nodeType,
    node.data.nodeName,
  )
  nodeRawdata.position = node.position
  nodeRawdata.transferInList = edges
    .filter(edge => edge.target === node.id)
    .map(edge => Number(edge.id))
  nodeRawdata.transferOutList = edges
    .filter(edge => edge.source === node.id)
    .map(edge => Number(edge.id))
  return nodeRawdata
}

export function transformEdgeWhenNewNode(edge: CustomEdgeType, _nodes: CustomNodeType[], _edges: CustomEdgeType[]) {
  const edgeRawData = createTransfer(edge.id, edge.source, edge.target)

  if (edge.data && edge.data.edgeName && edge.data.edgeName.length > 0) {
    edgeRawData.condition = {
      name: edge.data.edgeName!,
    }
  }
  return edgeRawData
}

export function transformFlowDataToRawData(nodes: CustomNodeType[], edges: CustomEdgeType[]) {
  // TODO: 由内向外转化时可能被删除。不应该默认input output 存在。校验逻辑应该增加输入输出节点是否存在的校验。
  // NOTE: 禁止输入输出被删除，所以默认 input output 存在
  // 或对newNode或editNode等操作行为添加新的flowdatatoRawData方法，保证此函数在调用前进行check,调用时input，output节点必定存在
  let start = null
  let end = null
  const actions: Array<RawAction> = []
  const transfers: Array<Transfer> = []
  const input = nodes.find(node => node.type === 'input') as InputNodeType
  const output = nodes.find(node => node.type === 'output') as OutputNodeType
  const inputRawNode = createRawNodeData(
    input.id,
    input.data.nodeType,
    input.data.nodeName,
  )
  const nextEdges = edges.filter(edge => edge.source === input.id)
  inputRawNode.transferOutList = nextEdges.map(edge => Number(edge.id))
  inputRawNode.position = input.position
  start = inputRawNode

  function transformEdge(outEdges: CustomEdgeType[]) {
    outEdges.forEach((edge) => {
      const transfer: Transfer = createTransfer(
        edge.id,
        edge.source,
        edge.target,
      )
      if (edge.data && edge.data.edgeName && edge.data.edgeName.length > 0) {
        transfer.condition = {
          name: edge.data.edgeName!,
        }
      }
      transfers.push(transfer)
    })
    // 可用于下一次递归的edge，非output
    const usefulEdges = outEdges.filter(edge => edge.target !== '1')
    usefulEdges.forEach((edge) => {
      const nextNodeId = edge.target
      const nextAction = actions.find(
        action => action.properties.id === Number.parseInt(nextNodeId),
      )
      if (nextAction) {
        nextAction.properties.transferInList.push(Number.parseInt(edge.id))
      }
      else {
        const nextNode = nodes.find(node => node.id === nextNodeId)
        if (nextNode) {
          const newAction: RawAction = {
            type: rawType[SAVE_TYPE_MAP[nextNode.data.nodeType]],
            properties: createRawNodeData(
              nextNode.id,
              nextNode.data.nodeType,
              nextNode.data.nodeName,
            ),
          }
          newAction.properties.transferInList.push(Number.parseInt(edge.id))
          const nextEdges = edges.filter(edge => edge.source === nextNode.id)
          newAction.properties.transferOutList = nextEdges.map(edge =>
            Number.parseInt(edge.id),
          )
          newAction.properties.position = nextNode.position
          actions.push(newAction)
          transformEdge(nextEdges)
        }
      }
    })
  }

  transformEdge(nextEdges)
  const outputRawNode = createRawNodeData(
    output.id,
    output.data.nodeType,
    output.data.nodeName,
  )
  const outputTransferIn = edges.filter(edge => edge.target === output.id)
  outputRawNode.transferInList = outputTransferIn.map(edge =>
    Number.parseInt(edge.id),
  )
  outputRawNode.position = output.position
  end = outputRawNode
  return {
    start,
    end,
    actions,
    transfers,
  }
}

function createTransfer(id: string, source: string, target: string): Transfer {
  return {
    id: Number.parseInt(id),
    fromNodeId: Number.parseInt(source),
    toNodeId: Number.parseInt(target),
    condition: null,
  }
}

function createRawNodeData(id: string, type: keyof typeof NODE_TYPE, name: string): RawNode {
  return {
    id: Number.parseInt(id),
    name,
    transferInList: [],
    transferOutList: [],
    type: SAVE_TYPE_MAP[type],
  }
}
